import styled, { keyframes, css } from "styled-components";

// Keyframes for header animations
const slideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-100%);
    opacity: 0;
  }
`;

// Nove animacije za NavContainer
const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`;

const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`;

const slideInFromTop = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const navContainerFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-15px) scale(0.98);
    filter: blur(1px);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0px);
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(255, 107, 53, 0.1);
  }
  50% {
    box-shadow: 0 0 30px rgba(255, 107, 53, 0.2);
  }
`;

export const HeaderWrapper = styled.header`
  background: linear-gradient(
    135deg,
    rgba(26, 26, 26, 0.95) 0%,
    rgba(44, 44, 44, 0.95) 100%
  );
  backdrop-filter: blur(20px);
  padding: 0.65rem 0;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  border-bottom: 2px solid #ff6b35;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(255, 107, 53, 0.1);

  /* Ključne promene za smooth animaciju */
  transform: ${(props) =>
    props.$isVisible ? "translateY(0)" : "translateY(-100%)"};
  opacity: ${(props) => (props.$isVisible ? 1 : 0)};

  /* Osnovna tranzicija - bez tranzicije kada je na vrhu ili tokom navigacije */
  transition: ${(props) =>
    props.$isAtTop || props.$isNavigating
      ? "none"
      : "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"};

  /* Specifične animacije na osnovu pravca skrolovanja */
  ${(props) => {
    // NEMA animacije ako je na vrhu stranice ili tokom navigacije
    if (props.$isAtTop || props.$isNavigating) {
      return css`
        animation: none;
      `;
    }

    // Animacija kada se header pokazuje (scroll up) - ali samo ako NIJE na vrhu
    if (
      props.$isVisible &&
      props.$scrollDirection === "up" &&
      !props.$isAtTop
    ) {
      return css`
        animation: ${slideDown} 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)
          forwards;
      `;
    }
    // Animacija kada se header skriva (scroll down)
    if (!props.$isVisible && props.$scrollDirection === "down") {
      return css`
        animation: ${slideUp} 0.4s cubic-bezier(0.55, 0.06, 0.68, 0.19) forwards;
      `;
    }
    return css`
      animation: none;
    `;
  }}

  /* Poboljšana pozadina na osnovu stanja skrolovanja */
  background: ${(props) => {
    if (!props.$isVisible) {
      return "linear-gradient(135deg, rgba(26, 26, 26, 0.7) 0%, rgba(44, 44, 44, 0.7) 100%)";
    }
    return "linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(44, 44, 44, 0.95) 100%)";
  }};

  backdrop-filter: ${(props) =>
    props.$isVisible ? "blur(20px)" : "blur(10px)"};

  @media (max-width: 900px) {
    padding: 0.8rem 0;
  }
`;

export const NavContainer = styled.nav`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;

  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  /* Osnovna animacija za container */
  animation: ${navContainerFadeIn} 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  /* Dinamička animacija na osnovu stanja header-a */
  opacity: ${(props) => (props.$isVisible !== false ? 1 : 0.95)};
  transform: ${(props) =>
    props.$isVisible !== false ? "scale(1)" : "scale(0.98)"};

  /* Smooth transition samo kada nije na vrhu */
  transition: ${(props) =>
    props.$isAtTop ? "none" : "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)"};

  /* Dodaj subtilan glow efekat kada je header vidljiv */
  ${(props) =>
    props.$isVisible &&
    !props.$isAtTop &&
    css`
      position: relative;

      &::before {
        content: "";
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        background: linear-gradient(
          135deg,
          rgba(255, 107, 53, 0.1) 0%,
          rgba(255, 140, 66, 0.05) 50%,
          rgba(255, 107, 53, 0.1) 100%
        );
        border-radius: 8px;
        z-index: -1;
        opacity: 0;
        animation: ${glowPulse} 3s ease-in-out infinite;
      }
    `}

  /* Staggered animations za child elemente - koristimo :first-child, :nth-child umesto komponenti */
  > *:first-child {
    animation: ${slideInFromLeft} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
    animation-delay: 0.1s;
    animation-fill-mode: both;
  }

  /* Menu animacija - drugi child (ul) */
  > *:nth-child(2) {
    animation: ${slideInFromTop} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    animation-delay: 0.3s;
    animation-fill-mode: both;
  }

  /* Actions animacija - treći child (div) */
  > *:nth-child(3) {
    animation: ${slideInFromRight} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
    animation-delay: 0.5s;
    animation-fill-mode: both;
  }

  @media (max-width: 900px) {
    padding: 0 1rem;

    /* Brže animacije na mobile */
    > *:first-child {
      animation-delay: 0.05s;
      animation-duration: 0.5s;
    }

    > *:nth-child(2) {
      animation-delay: 0.15s;
      animation-duration: 0.5s;
    }

    > *:nth-child(3) {
      animation-delay: 0.25s;
      animation-duration: 0.5s;
    }
  }
`;

export const Logo = styled.span`
  font-size: 2rem;
  color: #ff6b35;
  text-decoration: none;
  font-weight: bold;

  letter-spacing: 2px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  cursor: pointer;
  position: relative;
  display: inline-block;

  transition: all 0.3s ease-in-out;

  &:hover {
    transform: rotate(1deg);
    text-shadow: 2px 2px 8px rgba(255, 107, 53, 0.5),
      0 0 20px rgba(255, 107, 53, 0.3);
    color: #ff8c42;

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: rotate(0deg);
    transition-duration: 0.1s;
  }

  @media (max-width: 900px) {
    font-size: 1.8rem;
  }
`;

export const NavMenu = styled.ul`
  display: flex;
  list-style: none;
  gap: 2rem;

  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) !important;

  @media (max-width: 900px) {
    display: none;
  }
`;

export const StyledLi = styled.li`
  cursor: pointer;
  position: relative;
  opacity: 0;
  animation: ${slideInFromTop} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;

  /* Individual delays for stagger effect */
  &:nth-child(1) {
    animation-delay: 0.4s;
  }
  &:nth-child(2) {
    animation-delay: 0.5s;
  }
  &:nth-child(3) {
    animation-delay: 0.6s;
  }
  &:nth-child(4) {
    animation-delay: 0.7s;
  }
  &:nth-child(5) {
    animation-delay: 0.8s;
  }

  /* Hover animation za ceo li element */
  &:hover {
    transform: translateY(-3px) scale(1.05);
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  &:hover::before {
    opacity: 1;
    transform: scale(1);
  }
`;

export const StyledSpan = styled.span`
  color: ${(props) => (props.$active ? "#ff6b35" : "#fff")};
  text-decoration: none;
  font-family: "Poppins-Medium";
  position: relative;
  padding: 0.4rem 0;
  display: inline-block;

  overflow: hidden;

  /* Poboljšane transitions */
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

  &:hover {
    color: #ff6b35;
    transform: translateY(-2px);
  }

  /* Poboljšana underline animacija */
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: ${(props) => (props.$active ? "100%" : 0)};
    width: ${(props) => (props.$active ? "100%" : 0)};
    height: 3px;
    background: linear-gradient(90deg, #ff6b35, #ff8c42, #ff6b35);
    transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    border-radius: 2px;
    box-shadow: 0 2px 8px rgba(255, 107, 53, 0.4);
  }

  &:hover::after {
    width: 100%;
    background: linear-gradient(90deg, #ff8c42, #ff6b35, #ff8c42);
  }

  &:hover::before {
    left: 100%;
  }
`;

export const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;

  /* Poboljšana animacija za actions sekciju */
  opacity: 0;
  animation: ${slideInFromRight} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  animation-delay: 0.6s;

  /* Dodaj glow container efekat */
  &::before {
    content: "";
    position: absolute;
    top: -10px;
    right: -10px;
    width: calc(100% + 20px);
    height: calc(100% + 20px);
    background: radial-gradient(
      circle,
      rgba(255, 107, 53, 0.1) 0%,
      transparent 70%
    );
    border-radius: 50px;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
    pointer-events: none;
  }

  &:hover::before {
    opacity: 1;
  }

  /* Micro-animations za child elemente */
  > * {
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  /* Hover efekat za celu sekciju */
  &:hover > * {
    transform: scale(1.02);
  }

  @media (max-width: 900px) {
    animation-delay: 0.3s;
    animation-duration: 0.5s;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) !important;
  }
`;
export const Relative = styled.div`
  position: relative;
`;
export const CartButton = styled.button`
  background: linear-gradient(135deg, #ff6b35, #e55a2b);
  color: #ffffff;
  padding: 0.45rem 1.05rem;
  border: none;
  border-radius: 25px;
  font-family: "Poppins-Bold";
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  overflow: hidden;

  /* Poboljšane transitions */
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

  /* Dodaj box-shadow i border */
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);
  border: 2px solid transparent;

  .cart-price {
    font-weight: bold;
    font-size: 0.85rem;
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    z-index: 2;
  }

  /* Dodaj animated background */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.6s ease;
    z-index: 1;
  }

  /* Ikona animacija */
  svg {
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    z-index: 2;
  }

  &:hover {
    background: linear-gradient(135deg, #e55a2b, #ff6b35);
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 12px 25px rgba(255, 107, 53, 0.4);
    border-color: rgba(255, 255, 255, 0.3);

    .cart-price {
      transform: scale(1.08);
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }

    svg {
      transform: rotate(10deg) scale(1.1);
    }

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px) scale(1.01);
    transition-duration: 0.1s;
  }

  /* Pulse animacija kada ima stavki u korpi */
  &.has-items {
    animation: ${glowPulse} 2s ease-in-out infinite;
  }

  @media (max-width: 900px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;

    .cart-price {
      font-size: 0.75rem;
    }

    &:hover {
      transform: translateY(-1px) scale(1.02);
    }
  }
`;

export const CartBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  color: #ffffff;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Poppins-Bold";
  border: 2px solid #ffffff;
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.4);

  /* Pulse animation for attention */
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 4px 12px rgba(231, 76, 60, 0.4);
    }
    50% {
      transform: scale(1.1);
      box-shadow: 0 6px 16px rgba(231, 76, 60, 0.6);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 4px 12px rgba(231, 76, 60, 0.4);
    }
  }
`;

export const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #ffffff;
  font-size: 1.5rem;
  cursor: pointer;
  border-radius: 12px;
  position: relative;
  overflow: hidden;

  width: 32px;
  height: 32px;

  align-items: center;
  justify-content: center;

  border: 2px solid transparent;

  /* Ripple effect - poboljšan */
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: radial-gradient(
      circle,
      rgba(255, 107, 53, 0.4) 0%,
      rgba(255, 107, 53, 0.1) 70%,
      transparent 100%
    );
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    z-index: 0;
  }

  &:hover {
    background: rgba(255, 107, 53, 0.15);
    color: #ff6b35;
    transform: scale(1.1) rotate(5deg);
    border-color: rgba(255, 107, 53, 0.3);
    box-shadow: 0 4px 15px rgba(255, 107, 53, 0.2);

    svg {
      transform: scale(1.1);
    }

    &::before {
      width: 50px;
      height: 50px;
    }
  }

  &:active {
    transform: scale(1.05) rotate(0deg);
    transition-duration: 0.1s;

    svg {
      transform: scale(0.95);
    }
  }

  @media (max-width: 900px) {
    display: block;

    &:hover {
      transform: scale(1.08);
    }
  }
  @media (max-width: 768px) {
    padding-top: 2px;
  }
`;

// Mobile Menu Styles
const staggerIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const MobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: 1001;
  opacity: ${(props) => (props.$isOpen ? "1" : "0")};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s ease-in-out;

  @media (min-width: 900px) {
    display: none;
  }
`;

export const MobileMenuContent = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 100%);
  width: 100%;
  max-width: 250px;
  height: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  transform: ${(props) =>
    props.$isOpen ? "translateX(0)" : "translateX(-100%)"};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow-y: auto;
`;

export const MobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 1.5rem 1rem;
  border-bottom: 1px solid #333;
`;

export const MobileMenuLogo = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #ff6b35;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);

  &:hover {
    color: #ff8c42;
    transform: scale(1.05);
  }
`;

export const MobileMenuClose = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 107, 53, 0.1);
    color: #ff6b35;
    transform: rotate(90deg);
  }
`;

export const MobileMenuNav = styled.nav`
  flex: 1;
  padding: 2rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const MobileNavLink = styled.div`
  display: flex;
  align-items: center;
  padding: 1.25rem 1.5rem;
  color: ${(props) => (props.$active ? "#ff6b35" : "#cccccc")};
  background: ${(props) =>
    props.$active ? "rgba(255, 107, 53, 0.1)" : "transparent"};
  border-left: 4px solid
    ${(props) => (props.$active ? "#ff6b35" : "transparent")};
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  font-size: 1.1rem;
  font-family: "Poppins-Medium";
  opacity: 0;
  animation: ${staggerIn} 0.4s ease-out forwards;

  &:nth-child(1) {
    animation-delay: 0.1s;
  }
  &:nth-child(2) {
    animation-delay: 0.15s;
  }
  &:nth-child(3) {
    animation-delay: 0.2s;
  }
  &:nth-child(4) {
    animation-delay: 0.25s;
  }
  &:nth-child(5) {
    animation-delay: 0.3s;
  }

  &:hover {
    background: rgba(255, 107, 53, 0.15);
    color: #ff6b35;
    border-left-color: #ff6b35;
    transform: translateX(10px);
  }
`;

export const MobileMenuCart = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  background: linear-gradient(135deg, #ff6b35, #e55a2b);
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: bold;
  margin: 1rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);

  &:hover {
    background: linear-gradient(135deg, #e55a2b, #ff6b35);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 107, 53, 0.4);
  }

  svg {
    font-size: 1.2rem;
    margin-right: 0.5rem;
  }
`;

export const HeaderBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: inherit;
  backdrop-filter: inherit;
  z-index: -1;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 107, 53, 0.05) 0%,
      rgba(255, 140, 66, 0.02) 100%
    );
    opacity: ${(props) => (props.$isVisible ? 1 : 0)};
    transition: opacity 0.3s ease;
  }
`;
